import { useId, useState } from 'react';
import { buildSmoothPath } from '~/app/dashboard/components/supervisorDashboard.utils';

type IncidentLineChartProps = {
  counts: number[];
  labels: string[];
};

export function IncidentLineChart({ counts, labels }: IncidentLineChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const chartId = useId().replace(/:/g, '');

  const width = 800;
  const height = 200;
  const paddingLeft = 36;
  const paddingRight = 12;
  const paddingTop = 20;
  const paddingBottom = 28;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const pointCount = counts.length;

  if (pointCount === 0) {
    return null;
  }

  const maxValue = Math.max(...counts, 1);
  const areaGradientId = `${chartId}-incidentChartGrad`;
  const lineGradientId = `${chartId}-lineStrokeGrad`;

  const xPos = (index: number) =>
    paddingLeft + (index / Math.max(pointCount - 1, 1)) * chartWidth;
  const yPos = (value: number) =>
    paddingTop + chartHeight - (value / maxValue) * chartHeight;

  const points = counts.map((value, index) => ({
    x: xPos(index),
    y: yPos(value),
  }));
  const linePath = buildSmoothPath(points);

  const baseY = paddingTop + chartHeight;
  const curveCommands = linePath.slice(linePath.indexOf(' ') + 1);
  const areaPath =
    `M${xPos(0).toFixed(2)},${baseY.toFixed(2)} ` +
    `L${xPos(0).toFixed(2)},${yPos(counts[0] ?? 0).toFixed(2)} ` +
    curveCommands +
    ` L${xPos(pointCount - 1).toFixed(2)},${baseY.toFixed(2)} Z`;

  const maxTicks = 4;
  const tickStep = Math.ceil(maxValue / maxTicks);
  const yTickValues: number[] = [];

  for (let value = tickStep; value <= maxValue; value += tickStep) {
    yTickValues.push(value);
  }

  const tooltipCount = hoveredIdx !== null ? (counts[hoveredIdx] ?? 0) : 0;
  const tooltipLabel = hoveredIdx !== null ? (labels[hoveredIdx] ?? '') : '';
  const tooltipX = hoveredIdx !== null ? xPos(hoveredIdx) : 0;
  const tooltipY = hoveredIdx !== null ? yPos(tooltipCount) : 0;
  const tooltipWidth = 72;
  const tooltipHeight = 38;
  const tooltipXAdjusted = Math.min(
    tooltipX - tooltipWidth / 2,
    width - paddingRight - tooltipWidth,
  );
  const tooltipXFinal = Math.max(tooltipXAdjusted, paddingLeft);
  const tooltipYFinal = tooltipY - tooltipHeight - 10;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{
        width: '100%',
        height: 'auto',
        display: 'block',
        overflow: 'visible',
      }}
    >
      <defs>
        <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1e6df6" stopOpacity={0.24} />
          <stop offset="58%" stopColor="#38bdf8" stopOpacity={0.1} />
          <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
        </linearGradient>
        <linearGradient id={lineGradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="50%" stopColor="#1e6df6" />
          <stop offset="100%" stopColor="#0f9d8a" />
        </linearGradient>
      </defs>

      <line
        x1={paddingLeft}
        y1={paddingTop + chartHeight}
        x2={width - paddingRight}
        y2={paddingTop + chartHeight}
        stroke="#d1dae6"
        strokeWidth={1.5}
      />
      {yTickValues.map((value) => {
        const yCoord = yPos(value);

        return (
          <g key={value}>
            <line
              x1={paddingLeft}
              y1={yCoord}
              x2={width - paddingRight}
              y2={yCoord}
              stroke="#edf2f8"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            <text
              x={paddingLeft - 6}
              y={yCoord + 4}
              textAnchor="end"
              fontSize={10}
              fill="#8d9aa8"
            >
              {value}
            </text>
          </g>
        );
      })}

      <path d={areaPath} fill={`url(#${areaGradientId})`} />

      <path
        d={linePath}
        fill="none"
        stroke={`url(#${lineGradientId})`}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {labels.map((label, index) => {
        const targetVisibleLabelCount = pointCount > 12 ? 6 : pointCount;
        const labelStep = Math.max(
          1,
          Math.ceil(pointCount / Math.max(targetVisibleLabelCount, 1)),
        );
        const shouldRenderLabel =
          index === 0 || index === pointCount - 1 || index % labelStep === 0;

        if (!shouldRenderLabel) {
          return null;
        }

        return (
          <text
            key={label + index}
            x={xPos(index)}
            y={height - 4}
            textAnchor={
              index === pointCount - 1
                ? 'end'
                : index === 0
                  ? 'start'
                  : 'middle'
            }
            fontSize={10}
            fill="#607081"
          >
            {label}
          </text>
        );
      })}

      {hoveredIdx !== null ? (
        <g>
          <line
            x1={tooltipX}
            y1={paddingTop}
            x2={tooltipX}
            y2={paddingTop + chartHeight}
            stroke="#8bb4ff"
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.8}
          />
          <circle
            cx={tooltipX}
            cy={tooltipY}
            r={4.5}
            fill="#ffffff"
            stroke="#1e6df6"
            strokeWidth={2}
          />
        </g>
      ) : null}

      {counts.map((_, index) => {
        const slotWidth = chartWidth / pointCount;

        return (
          <rect
            key={`hit-${index}`}
            x={xPos(index) - slotWidth / 2}
            y={paddingTop}
            width={slotWidth}
            height={chartHeight}
            fill="transparent"
            style={{ cursor: 'crosshair' }}
            onMouseEnter={() => setHoveredIdx(index)}
            onMouseLeave={() => setHoveredIdx(null)}
          />
        );
      })}

      {hoveredIdx !== null ? (
        <g>
          <rect
            x={tooltipXFinal}
            y={tooltipYFinal}
            width={tooltipWidth}
            height={tooltipHeight}
            rx={5}
            fill="#ffffff"
            stroke="#d7e4f2"
            opacity={0.98}
          />
          <text
            x={tooltipXFinal + tooltipWidth / 2}
            y={tooltipYFinal + 14}
            textAnchor="middle"
            fontSize={9}
            fill="#607081"
          >
            {tooltipLabel}
          </text>
          <text
            x={tooltipXFinal + tooltipWidth / 2}
            y={tooltipYFinal + 27}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={11}
            fontWeight="600"
            fill="#11263c"
          >
            {tooltipCount}
          </text>
        </g>
      ) : null}
    </svg>
  );
}
